import { useDispatch, useSelector } from "react-redux";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";

// Redux actions
import { getContactsFunc } from "../../../store/custom-actions/contact-actions/GetContactsSlicer";
import { deleteContactFunc } from "../../../store/custom-actions/contact-actions/DeleteContactSlicer";
import { readMessageFunc } from "../../../store/custom-actions/contact-actions/ReadMessageSlicer";

// Utils
import { handleDeleteEntity } from "../../../utils/deleteHelper";
import checkPermissions from "../../global-functions/checkPermissionsFunc";

// UI Components
import PageTitle from "../../layouts/PageTitle";
import SearchBox from "../../components/ui/SearchBox";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";

// ---------------- Message Content Helper ----------------
const formatContactData = (data) => {
  if (!data || typeof data !== 'object') return null;
  
  const formattedData = [];
  Object.entries(data).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      // Convert camelCase to readable format
      const readableKey = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      formattedData.push({ key: readableKey, value });
    }
  });
  return formattedData;
};

// ---------------- Message Modal ----------------
function MessageContent({ show, onHide, data }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  // Format additional data if exists
  const additionalData = data?.data ? formatContactData(data.data) : null;

  return (
    <Modal
      centered
      show={show}
      onHide={onHide}
      contentClassName={`${isRTL ? "rtl" : "ltr"} shadow-lg`}
      dir={i18n.dir()}
      size="lg"
    >
      <Modal.Header className="border-bottom">
        <Modal.Title className="text-lg font-semibold d-flex align-items-center">
          <i className="fas fa-envelope text-primary me-2"></i>
          {t("Message Details")}
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
        ></button>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Contact Information */}
        <div className="row mb-4">
          <div className="col-12">
            <h6 className="text-primary mb-3">
              <i className="fas fa-user me-2"></i>
              {t("Contact Information")}
            </h6>
          </div>
          
          {/* Name */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-user text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Name")}</small>
                <span className="fw-semibold">{data?.name || t("Not provided")}</span>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-envelope text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Email")}</small>
                <span className="fw-semibold">
                  {data?.email ? (
                    <a href={`mailto:${data.email}`} className="text-primary text-decoration-none">
                      {data.email}
                    </a>
                  ) : (
                    <span className="text-muted">{t("Not provided")}</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Phone */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-phone text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Phone")}</small>
                <span className="fw-semibold">
                  {data?.phone ? (
                    <a href={`tel:${data.phone}`} className="text-primary text-decoration-none">
                      {data.phone}
                    </a>
                  ) : (
                    <span className="text-muted">{t("Not provided")}</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-tag text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Subject")}</small>
                <span className="fw-semibold">
                  {data?.subject || <span className="text-muted">{t("No subject")}</span>}
                </span>
              </div>
            </div>
          </div>

          {/* Date */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-calendar text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Date")}</small>
                <span className="fw-semibold">
                  {data?.created_at ? new Date(data.created_at).toLocaleDateString(i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : t("Not available")}
                </span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="col-md-6 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-eye text-muted me-2"></i>
              <div>
                <small className="text-muted d-block">{t("Status")}</small>
                <span className={`badge ${data?.is_read ? "bg-success" : "bg-warning"} rounded-pill`}>
                  {data?.is_read ? t("Read") : t("Unread")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Data */}
        {additionalData && additionalData.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-primary mb-3">
                <i className="fas fa-info-circle me-2"></i>
                {t("Additional Information")}
              </h6>
              <div className="row">
                {additionalData.map((item, index) => (
                  <div key={index} className="col-md-6 mb-2">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-circle text-muted me-2" style={{ fontSize: '6px' }}></i>
                      <div>
                        <small className="text-muted d-block">{item.key}</small>
                        <span className="fw-semibold">{item.value}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Message Content */}
        <div className="row">
          <div className="col-12">
            <h6 className="text-primary mb-3">
              <i className="fas fa-comment me-2"></i>
              {t("Message")}
            </h6>
            <div className="p-4 bg-white rounded-lg border">
              <p className="mb-0 whitespace-pre-line leading-relaxed" style={{ lineHeight: '1.6' }}>
                {data?.message || <span className="text-muted">{t("No message content")}</span>}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onHide}
        >
          <i className="fas fa-times me-2"></i>
          {t("Close")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------- Main Page ----------------
function MessagesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State
  const [searchValue, setSearchValue] = useState("");
  const [contactsList, setContactsList] = useState([]);
  const [contactsWithMessage, setContactsWithMessage] = useState({});
  const [showMessageContent, setShowMessageContent] = useState(false);

  // Pagination
  const sort = 20;
  const activePag = useRef(0);
  const jobData = useRef([]);

  // Redux state
  const { loading: getContactsLoading, data: contactsData } = useSelector(
    (state) => state.getContactsSlicer
  );
  const { loading: deleteLoading } = useSelector(
    (state) => state.deleteContactSlicer
  );

  // Fetch contacts
  useEffect(() => {
    dispatch(getContactsFunc());
  }, [dispatch]);

  // Update contacts data
  useEffect(() => {
    if (contactsData) {
      setContactsList(contactsData);
      jobData.current = contactsData.slice(
        activePag.current * sort,
        (activePag.current + 1) * sort
      );
    }
  }, [contactsData]);

  // Handlers
  const handleSearch = () => {
    const query = searchValue ? `?&search=${searchValue}` : "";
    dispatch(getContactsFunc(query));
  };

  const handlePageClick = (i) => {
    activePag.current = i;
    jobData.current = contactsList.slice(
      activePag.current * sort,
      (activePag.current + 1) * sort
    );
  };

  const handleDeleteItem = (id, entityName = "") => {
    handleDeleteEntity({
      id,
      entityName,
      deleteFunc: deleteContactFunc,
      dispatch,
      refetchFunc: getContactsFunc,
      t,
    });
  };

  const handleReadMessage = async (message) => {
    const result = await dispatch(
      readMessageFunc({ id: message.id, payload: { is_read: 1 } })
    );
    if (result.payload?.success) {
      setContactsWithMessage(message);
      setShowMessageContent(true);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate()} / ${date.getMonth() + 1} / ${date.getFullYear()}`;
  };

  // ---------------- Render ----------------
  return (
    <>
      <PageTitle motherMenu="Dashboard" activeMenu="Contacts" />

      <div className="col-12">
        <div className="card">
          {/* Header with search */}
          <div className="card-header flex justify-between items-center">
            <SearchBox
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              onSearch={handleSearch}
            />
          </div>

          {/* Table */}
          <div className="card-body">
            <div className="w-100 table-responsive">
              <Table
                showIndex
                startIndex={activePag.current * sort}
                columns={[
                  {
                    header: t("Subject"),
                    render: (row) => row?.subject ?? "-",
                  },
                  { header: t("Email"), render: (row) => row.email },
                  { header: t("Phone"), render: (row) => row.phone },
                  {
                    header: t("Message Date"),
                    render: (row) => formatDate(row?.created_at),
                  },
                  {
                    header: t("Status"),
                    render: (row) => (
                      <span
                        className={`badge ${
                          row.is_read ? "bg-success" : "bg-danger"
                        } rounded-pill`}
                      >
                        {row.is_read ? t("Opened") : t("Not reading")}
                      </span>
                    ),
                  },
                ]}
                data={jobData.current}
                loading={getContactsLoading}
                actions={(contact) => (
                  <>
                    {checkPermissions(
                      "read contacts",
                      <Link
                        to="#"
                        className="btn btn-primary shadow btn-xs sharp me-1"
                        onClick={() => handleReadMessage(contact)}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                    )}
                    {checkPermissions(
                      "delete contacts",
                      <Link
                        to="#"
                        className="btn btn-danger shadow btn-xs sharp"
                        onClick={() =>
                          handleDeleteItem(contact.id, contact.name)
                        }
                      >
                        <i className="fa fa-trash"></i>
                      </Link>
                    )}
                  </>
                )}
              />

              {/* Pagination */}
              <Pagination
                totalItems={contactsList.length}
                currentPage={activePag.current}
                perPage={sort}
                onPageChange={handlePageClick}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Loading + Modal */}
      <LoadingScreen loading={deleteLoading} />
      <MessageContent
        show={showMessageContent}
        onHide={() => setShowMessageContent(false)}
        data={contactsWithMessage}
      />
    </>
  );
}

export default MessagesPage;
